import "./EditableMetadocNameCell.scss";

import cx from "classnames";
import { KaboodleToaster } from "components/common";
import { useCallback, useEffect, useRef, useState } from "react";
import { useUpdateMetadocMutation } from "services/metadoc-api";
import {
  METADOC_NAME_MAX_LENGTH,
  metadocNameSchema,
} from "./metadocNameValidation";

type EditableMetadocNameCellProps = {
  metadocId: number;
  metadocName: string;
  documentCount?: number;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
};

const EditableMetadocNameCell = ({
  metadocId,
  metadocName,
  documentCount,
  className,
  onClick,
}: EditableMetadocNameCellProps) => {
  const [updateMetadoc] = useUpdateMetadocMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(metadocName);
  const [isHovered, setIsHovered] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // reset value when metadoc name changes
  useEffect(() => {
    setValue(metadocName);
  }, [metadocName]);

  const handleEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (isSaving) return;

    const nameValidation = metadocNameSchema.safeParse(value);
    if (!nameValidation.success) {
      KaboodleToaster.error(
        nameValidation.error.issues[0]?.message ?? "Invalid metadoc name",
      );
      setValue(metadocName);
      setIsEditing(false);
      return;
    }
    const trimmedValue = nameValidation.data;

    if (trimmedValue === metadocName) {
      setIsEditing(false);
      return;
    }

    try {
      setIsSaving(true);
      await updateMetadoc({
        metadocId,
        data: {
          name: trimmedValue,
        },
      }).unwrap();

      setIsEditing(false);
      KaboodleToaster.success("Metadoc name updated successfully");
    } catch (error) {
      console.error("Failed to update metadoc name:", error);
      KaboodleToaster.error("Failed to update metadoc name");
      setValue(metadocName);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  }, [metadocId, metadocName, value, updateMetadoc, isSaving]);

  const handleCancel = useCallback(() => {
    setValue(metadocName);
    setIsEditing(false);
  }, [metadocName]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        handleSave();
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleCancel();
      }
    },
    [handleSave, handleCancel],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (newValue.length <= METADOC_NAME_MAX_LENGTH) {
        setValue(newValue);
      }
    },
    [],
  );

  // focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <div
        className={cx("editable-metadoc-name-cell", className)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          className="editable-metadoc-name-cell__input"
          disabled={isSaving}
          maxLength={METADOC_NAME_MAX_LENGTH}
        />
        {isSaving && (
          <span className="material-icons editable-metadoc-name-cell__saving">
            hourglass_empty
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      className={cx("editable-metadoc-name-cell", className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <div className="editable-metadoc-name-cell__content">
        <span className="editable-metadoc-name-cell__text">{metadocName}</span>
        {documentCount !== undefined && (
          <span className="editable-metadoc-name-cell__count">
            ({documentCount} document{documentCount !== 1 ? "s" : ""})
          </span>
        )}
      </div>
      <button
        className={cx("editable-metadoc-name-cell__edit-button", {
          "editable-metadoc-name-cell__edit-button--visible": isHovered,
        })}
        onClick={handleEdit}
        title="Edit metadoc name"
      >
        <span className="material-icons">edit</span>
      </button>
    </div>
  );
};

export default EditableMetadocNameCell;
